import { useState } from "react";
import { Download, Smartphone, Share, CheckCircle2 } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useAirtimeLink, AIRTIME_URL } from "@/constants/links";
import { ShareLinks } from "./ShareLinks";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function AppDownloads() {
  const { ready, installed, isIOS, install } = useInstallPrompt();
  const airtime = useAirtimeLink();
  const [help, setHelp] = useState(false);
  const onInstall = async () => { if (ready) { await install(); } else { setHelp(true); } };

  return (
    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="app-downloads">
      <article className="flex min-w-0 items-center gap-3 rounded-2xl border border-brand-line bg-white p-4 shadow-subtle sm:gap-4 sm:p-5" data-testid="install-card">
        <img src="/icon-192.png" alt="Bhasa Care app icon" className="h-12 w-12 shrink-0 rounded-2xl sm:h-14 sm:w-14" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-brand-navy">Bhasa Care app</p>
          <p className="text-xs text-brand-slate">{installed ? "Installed on this device." : "Get visit updates on your home screen. No app store needed."}</p>
        </div>
        {installed ? <CheckCircle2 className="h-6 w-6 text-brand-emerald" data-testid="install-done" /> : (
          <button onClick={onInstall} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-blueHover" data-testid="install-app-button"><Download className="h-4 w-4" />Download</button>
        )}
      </article>

      <article className="flex min-w-0 items-center gap-3 rounded-2xl border border-brand-line bg-white p-4 shadow-subtle sm:gap-4 sm:p-5" data-testid="airtime-card">
        <span className="flex h-12 w-12 shrink-0 items-center sm:h-14 sm:w-14 justify-center rounded-2xl bg-brand-emerald text-white"><Smartphone className="h-7 w-7" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-brand-navy">Bhasa Airtime</p>
          <p className="text-xs text-brand-slate">{airtime ? "Send airtime and data to family in Zimbabwe." : "Coming soon — download here first, then on Play Store."}</p>
        </div>
        {airtime ? (
          <a href={airtime} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-emeraldHover" data-testid="airtime-download-button"><Download className="h-4 w-4" />Download</a>
        ) : (
          <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-brand-slate" data-testid="airtime-coming-soon" title={AIRTIME_URL}>Coming soon</span>
        )}
      </article>

      <ShareLinks />

      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="install-help-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Bhasa to your home screen</DialogTitle><DialogDescription>Installs like an app — no app store needed.</DialogDescription></DialogHeader>
          {isIOS ? (
            <ol className="list-decimal space-y-2 pl-5 text-sm text-brand-navy">
              <li>Tap the <Share className="inline h-4 w-4" /> <strong>Share</strong> button at the bottom of Safari.</li>
              <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong>. Bhasa Care appears with your other apps.</li>
            </ol>
          ) : (
            <ol className="list-decimal space-y-2 pl-5 text-sm text-brand-navy">
              <li>Open the browser menu (⋮ in Chrome).</li>
              <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
              <li>Confirm. Bhasa Care appears with your other apps.</li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
